import React, {
 FunctionComponent, Fragment, useState, useEffect
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import { SnmpUserPrivileges, SnmpV3User } from 'data/snmp/snmp.types';
import * as actions from 'data/snmp/snmp.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as selectors from 'data/snmp/snmp.selectors';
import { snmpUserColumnDefs } from 'routes/SnmpUser/SnmpUser.types';
import { AppToaster } from 'shared/toaster';
import {
 AuditActionType, AuditService, getAuditDetails, getAuditFieldInfo
} from 'shared/userAudit/audit-constants';
import SnmpUserListGrid from './components/SnmpUserGrid';

type RouterParams = {
  history: string;
};

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  deliveryFunctionId: state.global.selectedDF,
  selectedSnmpUser: selectors.getSelectedSnmpUser(state),
  data: selectors.getSnmpUsers(state)
});

const mapDispatch = {
  goToSnmpUserCreate: actions.goToSnmpUserCreate,
  goToSnmpUserEdit: actions.goToSnmpUserEdit,
  goToSnmpUserView: actions.goToSnmpUserView,
  goToSmnpUserList: actions.goToSmnpUserList,
  getSnmpUsers: actions.getSnmpUsers,
  deleteSnmpUser: actions.deleteSnmpUser,
  selectSnmpUser: actions.selectSnmpUser
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;

export type Props = PropsFromRedux & PropsFromRouter;

export const SnmpUserList: FunctionComponent<Props> = ({
  privileges,
  isAuditEnabled,
  userId,
  userName,
  history,
  data,
  beforeLogout,
  deliveryFunctionId,
  goToSnmpUserEdit,
  goToSnmpUserView,
  goToSnmpUserCreate,
  getSnmpUsers,
  deleteSnmpUser,
  selectedSnmpUser,
  selectSnmpUser
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [columnDefs] = useState<ColDef[]>(snmpUserColumnDefs);

  useEffect(() => {
    getSnmpUsers(deliveryFunctionId);
    return () => {
      AppToaster.clear();
    };
  }, [deliveryFunctionId, getSnmpUsers]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  const handleRefresh = () => getSnmpUsers(deliveryFunctionId);

  const handleCreateNew = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    goToSnmpUserCreate(history);
  };

  const handleDeleteSnmpUser = () => {
    const snmpUserId = selectedSnmpUser?.id;
    if (!snmpUserId) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        snmpUserId,
        AuditService.SNMP_USER,
        AuditActionType.SNMP_USER_DELETE
      );
      body.auditDetails = auditDetails as AuditDetails;

      deleteSnmpUser(
        deliveryFunctionId,
        body,
        selectedSnmpUser as SnmpV3User
      );
    }
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectSnmpUser(event.api.getSelectedRows()[0]);
  };

  const handleGoToEdit = () => goToSnmpUserEdit(history);

  const handleGoToView = () => goToSnmpUserView(history);

  const hasPrivilegeToView = privileges.includes(SnmpUserPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(SnmpUserPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(SnmpUserPrivileges.Delete);

  return (
    <Fragment>
      <SnmpUserListGrid
        data={data}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        lastUpdatedAt={lastUpdatedAt}
        selectedSnmpUser={selectedSnmpUser}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onDelete={handleDeleteSnmpUser}
        onEdit={handleGoToEdit}
        onView={handleGoToView}
      />
    </Fragment>
  );
};

export default withRouter(connector(SnmpUserList));
